class JsBannerProducts extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-js-banner-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-js-banner-dot]'));
    this.track = this.querySelector('[data-js-banner-track]');
    if (this.slides.length < 2) return;

    this.index = 0;
    // Mobile shows a peeking rail, so the dots scroll it; desktop swaps one card.
    this.peek = window.matchMedia('(max-width: 999px)');
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.go(i));
    });

    if (this.track) {
      let raf = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          if (!this.peek.matches) return;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this.syncFromScroll());
        },
        { passive: true }
      );
    }

    this.peek.addEventListener('change', () => this.apply());
    this.apply();
    this.startAutoplay();

    let startX = 0;
    this.addEventListener(
      'touchstart',
      (event) => {
        startX = event.changedTouches[0].clientX;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        if (this.peek.matches) return; // the rail scrolls natively
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) < 40) return;
        const next = delta < 0 ? this.index + 1 : this.index - 1;
        this.go((next + this.slides.length) % this.slides.length);
      },
      { passive: true }
    );
  }

  // Advance every N seconds (section setting, 0 = off). Pauses while the cards are hovered,
  // focused or touched, while the tab is hidden, and never runs for reduced-motion visitors.
  startAutoplay() {
    const seconds = parseInt(this.dataset.autoplay, 10) || 0;
    if (seconds <= 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayDelay = seconds * 1000;
    this.paused = false;
    const pause = () => {
      this.paused = true;
    };
    const resume = () => {
      this.paused = false;
      this.restartAutoplay();
    };
    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.addEventListener('touchstart', () => this.restartAutoplay(), { passive: true });
    document.addEventListener('visibilitychange', () => this.restartAutoplay());
    this.restartAutoplay();
  }

  restartAutoplay() {
    if (!this.autoplayDelay) return;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = setInterval(() => {
      if (this.paused || document.hidden) return;
      this.go((this.index + 1) % this.slides.length, { auto: true });
    }, this.autoplayDelay);
  }

  // on the rail every card stays in the flow; off it, only the current one renders
  apply() {
    const peek = this.peek.matches;
    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('hidden', !peek && i !== this.index);
    });
    this.markDots();
  }

  syncFromScroll() {
    const left = this.track.scrollLeft;
    let best = 0;
    let bestDelta = Infinity;
    this.slides.forEach((slide, i) => {
      const delta = Math.abs(slide.offsetLeft - this.track.offsetLeft - left);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = i;
      }
    });
    if (best === this.index) return;
    this.index = best;
    this.markDots();
  }

  markDots() {
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  go(index, { auto = false } = {}) {
    // a manual pick restarts the countdown so the card doesn't change straight after
    if (!auto) this.restartAutoplay();
    this.index = index;
    if (this.peek.matches && this.track) {
      const slide = this.slides[index];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.markDots();
      return;
    }
    this.apply();
  }
}

if (!customElements.get('js-banner-products')) {
  customElements.define('js-banner-products', JsBannerProducts);
}
